// BorrowerDetail.jsx — view borrower + collaterals + risk
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Box, Paper, Button, Chip, Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, Tooltip, IconButton,
} from "@mui/material";
import { ArrowLeft, Building2, Plus, Activity, ArrowRight } from "lucide-react";
import DashboardLayout from "../layouts/DashboardLayout";
import RiskPill from "../components/RiskPill";
import ScenarioToggle from "../components/ScenarioToggle";
import { borrowersApi, riskApi } from "../api/api";
import { fmtINR, fmtScore, fmtBps, HAZARD_LABELS } from "../utils/format";

export default function BorrowerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [risk, setRisk] = useState(null);
  const [scenario, setScenario] = useState("current");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [b, r] = await Promise.all([
          borrowersApi.get(id),
          riskApi.borrower(id, scenario),
        ]);
        setData(b.data);
        setRisk(r.data);
      } catch (e) {
        console.error(e);
      } finally { setLoading(false); }
    })();
  }, [id, scenario]);

  if (loading) {
    return <DashboardLayout title="Borrower"><Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={26} /></Box></DashboardLayout>;
  }

  const b = data?.borrower || {};
  const coll = data?.collaterals || [];
  const summary = risk?.summary || {};
  const scores = risk?.results || [];

  // map collateral->top score for display
  const scoreMap = {};
  scores.forEach((s) => { scoreMap[s.collateral_id] = s; });

  return (
    <DashboardLayout
      title={b.name || "Borrower"}
      subtitle={`${b.cif_no || ""} · ${(b.portfolio || "").toUpperCase()} · ${(b.industry || "").toUpperCase().replace("_", " ")}`}
      headerExtra={
        <Box sx={{ display: "flex", gap: 1.2, alignItems: "center" }}>
          <ScenarioToggle value={scenario} onChange={setScenario} />
          <Button size="small" variant="outlined" onClick={() => navigate("/borrowers")} startIcon={<ArrowLeft size={13} />}>
            Back
          </Button>
        </Box>
      }
    >
      {/* Borrower meta card */}
      <Paper sx={{ p: 2.6, mb: 2 }} data-testid="borrower-meta">
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: 2 }}>
          <Meta label="Outstanding" value={fmtINR(b.outstanding_inr)} />
          <Meta label="Sanctioned" value={fmtINR(b.total_exposure_inr)} />
          <Meta label="Facility" value={b.facility_type || "—"} />
          <Meta label="Credit Rating" value={b.credit_rating || "—"} />
          <Meta label="Tenor" value={b.tenor_years ? `${b.tenor_years}y` : "—"} />
          <Meta label="LTV" value={b.ltv_pct ? `${b.ltv_pct}%` : "—"} />
          <Meta label="Reg. Office" value={`${b.registered_office_state || ""} · ${b.registered_office_pin || ""}`} />
          <Meta label="RM" value={b.relationship_manager || "—"} />
        </Box>
      </Paper>

      {/* Climate risk summary */}
      <Paper sx={{ p: 2.6, mb: 2 }} data-testid="borrower-climate-summary">
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Box>
            <Box className="cb-display" sx={{ fontSize: 17, fontWeight: 600, color: "#111827" }}>Climate Physical Risk · {scenario}</Box>
            <Box sx={{ fontSize: 12, color: "#6B7280" }}>Aggregated across {summary.collateral_count || 0} collaterals</Box>
          </Box>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Box>
              <Box className="cb-kpi-label">Score</Box>
              <Box className="cb-mono" sx={{ fontSize: 22, fontWeight: 600 }}>{fmtScore(summary.avg_score)}</Box>
            </Box>
            <Box>
              <Box className="cb-kpi-label">Exposure</Box>
              <Box className="cb-mono" sx={{ fontSize: 18, fontWeight: 600 }}>{fmtINR(summary.total_financial_exposure_inr)}</Box>
            </Box>
            <Box>
              <Box className="cb-kpi-label">PD uplift</Box>
              <Box className="cb-mono" sx={{ fontSize: 18, fontWeight: 600, color: "#8F5E14" }}>{fmtBps(summary.avg_pd_uplift_bps)}</Box>
            </Box>
            <RiskPill level={summary.risk_level} />
          </Box>
        </Box>

        {/* Hazard breakdown bars */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6 }}>
          {(summary.hazard_breakdown || []).map((h) => (
            <Chip key={h.hazard_id}
              size="small"
              label={`${HAZARD_LABELS[h.hazard_id] || h.hazard_id} · ${fmtScore(h.avg_score)}`}
              sx={{
                fontSize: 11, fontFamily: "IBM Plex Mono, monospace",
                background: "#F0EFEA", color: "#111827",
              }} />
          ))}
          {(summary.hazard_breakdown || []).length === 0 && (
            <Box sx={{ fontSize: 12, color: "#94A3B8" }}>No risk scores yet — re-score from Dashboard or Collateral Registry.</Box>
          )}
        </Box>
      </Paper>

      {/* Collaterals */}
      <Paper sx={{ p: 0, overflow: "hidden" }} data-testid="borrower-collaterals">
        <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #F0EFEA" }}>
          <Box className="cb-display" sx={{ fontSize: 16, fontWeight: 600, color: "#111827" }}>
            <Building2 size={15} style={{ verticalAlign: "middle", marginRight: 6 }} />
            Collateral Pool · {coll.length}
          </Box>
          <Button size="small" variant="outlined"
            component={Link} to={`/collaterals?borrower_id=${id}`}
            startIcon={<Plus size={12} />}
            data-testid="add-collateral-for-borrower">
            Add collateral
          </Button>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Collateral</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>PIN · State</TableCell>
              <TableCell>Lat/Lon</TableCell>
              <TableCell align="right">Valuation</TableCell>
              <TableCell align="right">Score</TableCell>
              <TableCell align="right">PD Uplift</TableCell>
              <TableCell align="right">Risk</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {coll.length === 0 && <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: "#94A3B8" }}>No collaterals pledged yet.</TableCell></TableRow>}
            {coll.map((c) => {
              const s = scoreMap[c.collateral_id];
              return (
                <TableRow key={c.collateral_id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{c.name}</TableCell>
                  <TableCell sx={{ textTransform: "capitalize" }}>{c.type?.replace("_", " ")}</TableCell>
                  <TableCell><Box className="cb-mono" sx={{ fontSize: 11 }}>{c.pin_code || "—"}</Box><Box sx={{ fontSize: 11.5, color: "#6B7280" }}>{c.state}</Box></TableCell>
                  <TableCell className="cb-mono" sx={{ fontSize: 11 }}>{Number(c.latitude).toFixed(3)}, {Number(c.longitude).toFixed(3)}</TableCell>
                  <TableCell align="right" className="cb-mono">{fmtINR(c.current_valuation_inr)}</TableCell>
                  <TableCell align="right" className="cb-mono" sx={{ fontWeight: 600 }}>{fmtScore(s?.overall_score)}</TableCell>
                  <TableCell align="right" className="cb-mono">{s ? fmtBps(s.pd_uplift_bps) : "—"}</TableCell>
                  <TableCell align="right">{s ? <RiskPill level={s.risk_level} /> : <Chip size="small" label="UNSCORED" sx={{ fontSize: 9.5 }} />}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Open">
                      <IconButton size="small" onClick={() => navigate(`/collaterals/${c.collateral_id}`)} data-testid={`bdetail-open-${c.collateral_id}`}>
                        <ArrowRight size={14} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </DashboardLayout>
  );
}

function Meta({ label, value }) {
  return (
    <Box>
      <Box className="cb-kpi-label">{label}</Box>
      <Box className="cb-mono" sx={{ fontSize: 14, fontWeight: 600, color: "#111827", mt: 0.3 }}>{value}</Box>
    </Box>
  );
}
